import { Component, computed, inject, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ColumnConfig, FieldType, GenericTableComponent, GlobalAction, HeaderConfigurationService, ModalMode, ModalTabConfig, TableAction, TableActionConfig } from '@solcre-org/core-ui';
import { UserModel } from '../../../shared/models/user.model';
import { Validators } from '@angular/forms';
import { environment } from '../../../../environments/environment.local';
import { DateUtils } from '../../../shared/utils/date.utils';

@Component({
  selector: 'app-users',
  imports: [GenericTableComponent, TranslateModule],
  templateUrl: './users.html'
})
export class Users {
  private headerConfigurationService = inject(HeaderConfigurationService);
  private translateService: TranslateService = inject(TranslateService);

  endpoint = environment.API.USERS_URI;
  users = signal<UserModel[]>([]);
  roles = [
    { label: 'User', value: 'ROLE_USER' },
    { label: 'Analyst', value: 'ROLE_ANALYST' },
    { label: 'Manager', value: 'ROLE_MANAGER' },
    { label: 'Admin', value: 'ROLE_ADMIN' }
  ];
  columns: ColumnConfig<UserModel>[] = [
    {
      label: 'users.id',
      key: 'id',
    },
    {
      label: 'users.email',
      key: 'email',
    },
    {
      label: 'users.fullName',
      key: 'fullName',
    },
    {
      label: 'users.roles',
      key: 'roles',
      format: (value: any) => value.map((role: any) => {
        return this.roles.find((r) => r.value === role)?.label;
      })
    },
    {
      label: 'users.createdAt',
      key: 'createdAt',
      sortable: true,
      format: (value: any) => DateUtils.format(value)
    },
    {
      label: 'users.lastLoginAt',
      key: 'lastLoginAt',
      sortable: true,
      format: (value: any) => DateUtils.format(value)
    }
  ];

  modalFields = computed<ModalTabConfig<UserModel>[]>(() => [
    {
      tabId: 'general_data',
      tabLabel: 'currency.general_data',
      fields: [
        {
          label: 'users.email',
          placeholder: 'users.emailPlaceholder',
          key: 'email',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required, Validators.email],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
            email: this.translateService.instant('validators.email'),
          }
        },
        {
          label: 'users.firstName',
          placeholder: 'users.firstNamePlaceholder',
          key: 'firstName',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          }
        },
        {
          label: 'users.lastName',
          placeholder: 'users.lastNamePlaceholder',
          key: 'lastName',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          }
        },
        {
          label: 'users.roles',
          placeholder: 'users.rolesPlaceholder',
          key: 'roles',
          type: FieldType.SELECT,
          searchable: true,
          multiple: true,
          readonly: false,
          options: this.roles,
          defaultValue: '',
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          },
          modes: {
            [ModalMode.EDIT]: { visible: true, includeInPayload: true },
            [ModalMode.VIEW]: {
              visible: true, format: (value: any, row: any) => row.roles.map((role: any) => {
                return this.roles.find((r) => r.value === role)?.label;
              }).join(', ')
            },
          },
        },
        {
          label: 'users.createdAt',
          placeholder: 'users.createdAtPlaceholder',
          key: 'createdAt',
          type: FieldType.DATE,
          readonly: true,
          modes: {
            [ModalMode.CREATE]: { visible: false, includeInPayload: false },
            [ModalMode.EDIT]: { visible: false, includeInPayload: false },
            [ModalMode.VIEW]: { visible: true, format: (value: any, row: any) => DateUtils.format(row.createdAt), includeInPayload: false },
          },
        },
        {
          label: 'users.lastLoginAt',
          placeholder: 'users.lastLoginAtPlaceholder',
          key: 'lastLoginAt',
          type: FieldType.DATE,
          readonly: true,
          modes: {
            [ModalMode.CREATE]: { visible: false, includeInPayload: false },
            [ModalMode.EDIT]: { visible: false, includeInPayload: false },
            [ModalMode.VIEW]: { visible: true, format: (value: any, row: any) => DateUtils.format(row.lastLoginAt), includeInPayload: false },
          },
        },
        {
          label: 'users.password',
          placeholder: 'users.passwordPlaceholder',
          key: 'plainPassword',
          type: FieldType.PASSWORD,
          readonly: false,
          modes: {
            [ModalMode.CREATE]: {
              visible: true,
              includeInPayload: true,
              validators: [Validators.required, Validators.minLength(6)]
            },
            [ModalMode.EDIT]: {
              visible: true,
              includeInPayload: false,
              validators: [Validators.minLength(6)]
            },
            [ModalMode.VIEW]: {
              visible: false,
              includeInPayload: false
            },
          },
          errorMessages: {
            required: this.translateService.instant('validators.required'),
            minlength: this.translateService.instant('validators.minLength', { minLength: 6 }),
          }
        }
      ],
    },
  ] as any as ModalTabConfig<UserModel>[]);

  globalActions: GlobalAction<UserModel>[] = [];
  actions: TableActionConfig[] = [
    { action: TableAction.VIEW, isExtra: false },
    { action: TableAction.EDIT, isExtra: false },
    { action: TableAction.DELETE, isExtra: true },
    { action: TableAction.SELECT, isExtra: false },
    { action: TableAction.CREATE, isExtra: false },
  ];

  portsModelFactory = (data: Partial<UserModel>): UserModel => {
    return new UserModel().fromJSON(data);
  };

  ngOnInit() {
    this.headerConfigurationService.setEndpointHeaders(this.endpoint, {
      patch: [
        { 'Content-Type': 'application/merge-patch+json' },
      ]
    });
  }
}
